<template>
  <Transition name="fade">
    <div v-if="isLoading" class="spinner-overlay" @click.stop.prevent @touchmove.prevent>
      <div class="flex items-center">
        <object
          :data="AnimatedCatUrl"
          type="image/svg+xml"
          class="cat-spinner"
          aria-label="Loading..."
        ></object>

        <span class="text-xl mon text-center mt-3 -ml-3 loading-dots">
          {{ t('action.pleaseWait') }}
        </span>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { watch, onUnmounted } from 'vue'
import { useLoader } from '@/core/loader'
import AnimatedCatUrl from '@/assets/spinner/animated-cat-example.svg'
import { useI18n } from 'vue-i18n'

const { isLoading } = useLoader()
const { t } = useI18n()

// --- SCROLL LOCK LOGIC ---
// This watches the isLoading variable.
// If true: it sets body overflow to hidden (stops scrolling).
// If false: it restores scrolling.
watch(isLoading, (val) => {
  if (val) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Safety cleanup: If this component is ever destroyed while loading,
// ensure we release the scroll lock.
onUnmounted(() => {
  document.body.style.overflow = ''
})

defineOptions({
  name: 'AppSpinner',
})
</script>

<style scoped>
.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(10px);

  /* --- CLICK BLOCKING SETTINGS --- */
  /* 'auto' ensures this element captures all mouse events */
  pointer-events: auto;
  /* visual indicator that the user cannot click */
  cursor: wait;
}

.cat-spinner {
  width: 150px;
  height: 150px;
  /* We ignore clicks on the cat itself so they hit the overlay instead */
  pointer-events: none;
}

/* --- DOTS ANIMATION --- */
.loading-dots {
  position: relative;
  display: inline-block;
}

.loading-dots::after {
  content: '';
  position: absolute;
  left: 100%;
  animation: dots 2s infinite steps(1);
}

@keyframes dots {
  0% {
    content: '';
  }
  25% {
    content: '.';
  }
  50% {
    content: '..';
  }
  75% {
    content: '...';
  }
  100% {
    content: '';
  }
}

/* Transition effects */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
